import { useState } from "react";
import { Link } from "react-router-dom";
import { Palette, HelpCircle, ChevronDown, ChevronUp, Search } from "lucide-react";
import { Input } from "../components/ui/input";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "What is ColorWonder AI?",
          a: "ColorWonder AI is an AI-powered coloring book generator that creates unique, printable coloring pages based on your descriptions. Simply type what you want (like 'cute jungle animals' or 'magical unicorns'), and our AI generates beautiful line art perfect for coloring."
        },
        {
          q: "How do I create my first coloring book?",
          a: "1. Sign up for a free account\n2. Go to the Generator page\n3. Enter a theme or description (e.g., 'dinosaurs in a prehistoric jungle')\n4. Choose the number of pages (1-10)\n5. Click Generate and wait for your pages\n6. Download as a print-ready PDF!"
        },
        {
          q: "Is ColorWonder AI free to use?",
          a: "Yes! We offer a free plan that includes 5 pages per month with a limit of 2 pages per day. For more pages and features, check out our paid plans starting at $4.99/month."
        }
      ]
    },
    {
      category: "Pricing & Plans",
      questions: [
        {
          q: "What's included in each plan?",
          a: "• Free: 5 pages/month, 2 pages/day, basic quality, watermarked PDFs\n• Basic ($4.99/mo): 50 pages/month, 5 pages/day, standard quality\n• Plus ($9.99/mo): 120 pages/month, 10 pages/day, high quality, commercial license\n• Pro ($14.99/mo): 200 pages/month, 15 pages/day, premium quality, priority support"
        },
        {
          q: "Can I cancel my subscription anytime?",
          a: "Yes! You can cancel your subscription at any time from your dashboard. You'll continue to have access to your plan features until the end of your current billing period."
        },
        {
          q: "Do you offer refunds?",
          a: "We offer a 7-day money-back guarantee for new subscriptions. If you're not satisfied within the first 7 days and have generated fewer than 10 pages, you can request a full refund. See our Refund Policy for details."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) through our secure payment processor, Stripe."
        }
      ]
    },
    {
      category: "Using the Service",
      questions: [
        {
          q: "What kind of themes can I create?",
          a: "Almost anything! Popular themes include animals, superheroes, princesses, dinosaurs, space, underwater scenes, fairy tales, holidays, educational topics, and more. Just describe what you want, and our AI will create it."
        },
        {
          q: "How long does it take to generate pages?",
          a: "Each page typically takes 15-30 seconds to generate. A 10-page coloring book usually takes 2-5 minutes to complete."
        },
        {
          q: "Can I regenerate a page I don't like?",
          a: "Yes! If you're not happy with a specific page, you can regenerate it. This will use one page credit from your plan. Pro tip: Be more specific in your prompt for better results."
        },
        {
          q: "What format are the downloads?",
          a: "All coloring books are downloaded as print-ready PDF files in A4 format. The images are high-resolution and perfect for printing at home or at a print shop."
        }
      ]
    },
    {
      category: "Commercial Use & Licensing",
      questions: [
        {
          q: "Can I sell coloring books I create?",
          a: "Commercial use requires a Plus or Pro subscription. With these plans, you receive a commercial license that allows you to sell or distribute your generated coloring books."
        },
        {
          q: "Can I use ColorWonder for my business?",
          a: "Absolutely! Many teachers, publishers, and businesses use ColorWonder AI. The Plus and Pro plans include commercial licensing for business use."
        }
      ]
    },
    {
      category: "Technical & Account",
      questions: [
        {
          q: "I forgot my password. How do I reset it?",
          a: "Click 'Forgot Password' on the login page, enter your email address, and we'll send you a password reset link. The link expires after 1 hour for security."
        },
        {
          q: "Why can't I create a new account?",
          a: "We limit one free account per household to prevent abuse. If you're seeing an error, you may already have an account with a different email, or someone else on your network has a free account. Consider upgrading to a paid plan for additional accounts."
        },
        {
          q: "How do I delete my account?",
          a: "Contact us at support@colorwonder.app with your account email, and we'll process your deletion request within 48 hours. Note that this action is irreversible."
        }
      ]
    }
  ];

  const filteredFaqs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(
      faq => 
        faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full text-violet-700 font-medium text-sm mb-6">
              <HelpCircle className="w-4 h-4" />
              Help Center
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
              Find answers to common questions about ColorWonder AI
            </p>

            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full border-slate-200"
              />
            </div>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-8">
            {filteredFaqs.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-slate-100">
                <h2 className="text-xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  {category.category}
                </h2>
                <div className="space-y-3">
                  {category.questions.map((faq, questionIndex) => {
                    const key = `${categoryIndex}-${questionIndex}`;
                    const isOpen = openIndex === key;
                    
                    return (
                      <div
                        key={questionIndex}
                        className="border border-slate-100 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleQuestion(categoryIndex, questionIndex)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                        >
                          <span className="font-medium text-slate-800 pr-4">{faq.q}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4">
                            <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                              {faq.a}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-12 text-center bg-gradient-to-br from-violet-50 to-pink-50 rounded-3xl p-8 border border-violet-100">
            <h3 className="text-xl font-bold text-slate-800 mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Still have questions?
            </h3>
            <p className="text-slate-600 mb-6">
              Can't find what you're looking for? We're here to help!
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold hover:shadow-lg transition-all"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
